<script setup>
import { ref, computed, toRef, provide, onMounted, watch } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAgentsSidecar } from 'dashboard/composables/useAgentsSidecar';
import { messageStamp } from 'shared/helpers/timeHelper';

import AgentsThreadSelector from './AgentsThreadSelector.vue';
import AgentsGlobalQuery from './AgentsGlobalQuery.vue';
import AgentsLoader from './AgentsLoader.vue';
import AgentsEmptyState from './AgentsEmptyState.vue';
import AgentsEscalation from './AgentsEscalation.vue';
import AgentsSummary from './AgentsSummary.vue';
import AgentsDraftReply from './AgentsDraftReply.vue';
import AgentsSuggestions from './AgentsSuggestions.vue';
import AgentsConfig from './AgentsConfig.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  messages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['useDraft', 'applyAction']);

const formatTimestamp = timestamp => {
  if (!timestamp) return '';
  return messageStamp(timestamp, 'h:mm a');
};

const { updateUISettings } = useUISettings();

const conversationRef = toRef(props, 'conversation');
const messagesRef = toRef(props, 'messages');

const showConfig = ref(false);
const chatScrollContainer = ref(null);
const sidecarWidth = ref(360);
const isResizing = ref(false);

const {
  isLoading,
  isThreadLoading,
  isAnalyzing,
  isQuerying,
  error,
  currentStep,
  progress,
  summary,
  draftReply,
  suggestions,
  escalation,
  queryAnswer,
  chatHistory,
  currentThreadId,
  threads,
  hasResults,
  hasQueryAnswer,
  hasChatHistory,
  analyze,
  query,
  reset,
  clearChatHistory,
  createNewThread,
  switchThread,
  deleteThread,
  scrollToBottom,
} = useAgentsSidecar(conversationRef, messagesRef);

provide('agentsSidecar', {
  isLoading,
  isThreadLoading,
  isAnalyzing,
  isQuerying,
  error,
  currentStep,
  progress,
});

const closeSidebarPanel = () => {
  updateUISettings({
    is_agents_sidecar_open: false,
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

const handleUseDraft = text => {
  emit('useDraft', text);
};

const handleApplyAction = action => {
  emit('applyAction', action);
};

const handleQuery = queryText => {
  query(queryText);
};

const handleNewThread = async () => {
  await createNewThread();
};

const handleSwitchThread = threadId => {
  switchThread(threadId);
};

const handleDeleteThread = threadId => {
  deleteThread(threadId);
};

const showEmptyState = computed(() => {
  return (
    !isLoading.value &&
    !isThreadLoading.value &&
    !hasResults.value &&
    !hasQueryAnswer.value &&
    !hasChatHistory.value
  );
});

const resize = e => {
  if (!isResizing.value) return;
  const newWidth = window.innerWidth - e.clientX;
  if (newWidth >= 300 && newWidth <= 800) {
    sidecarWidth.value = newWidth;
  }
};

const stopResize = () => {
  isResizing.value = false;
  document.removeEventListener('mousemove', resize);
  document.removeEventListener('mouseup', stopResize);
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
  localStorage.setItem('agents_sidecar_width', sidecarWidth.value);
};

const startResize = () => {
  isResizing.value = true;
  document.addEventListener('mousemove', resize);
  document.addEventListener('mouseup', stopResize);
  document.body.style.cursor = 'ew-resize';
  document.body.style.userSelect = 'none';
};

watch(
  () => chatHistory.value.length,
  () => {
    scrollToBottom();
  }
);

watch(
  () => queryAnswer.value?.text,
  () => {
    scrollToBottom();
  }
);

onMounted(() => {
  const savedWidth = localStorage.getItem('agents_sidecar_width');
  if (savedWidth) {
    sidecarWidth.value = parseInt(savedWidth, 10);
  }
  scrollToBottom();
});
</script>

<template>
  <div
    class="relative flex flex-col h-full text-sm leading-6 tracking-tight bg-n-solid-1 border-l border-n-weak transition-all duration-75"
    :style="{ width: sidecarWidth + 'px', minWidth: sidecarWidth + 'px' }"
  >
    <!-- Resizer Handle -->
    <div
      class="absolute top-0 bottom-0 left-[-4px] w-[8px] cursor-ew-resize z-50 hover:bg-n-brand/10 transition-colors"
      @mousedown="startResize"
    />

    <div class="border-b border-n-weak">
      <AgentsThreadSelector
        :current-thread-id="currentThreadId"
        :threads="threads"
        @new-thread="handleNewThread"
        @switch-thread="handleSwitchThread"
        @delete-thread="handleDeleteThread"
        @toggle-config="showConfig = true"
        @refresh="analyze('full_analysis')"
        @reset="reset"
        @close="closeSidebarPanel"
      />
    </div>

    <div
      ref="chatScrollContainer"
      class="flex-1 flex flex-col overflow-y-auto px-4 py-4 gap-4 scroll-smooth min-h-0"
    >
      <div v-if="isThreadLoading" class="flex items-center justify-center py-8">
        <span class="i-lucide-loader-2 text-2xl text-n-brand animate-spin" />
      </div>

      <AgentsLoader
        v-if="isLoading"
        :step="currentStep"
        :progress="progress"
        :is-analyzing="isAnalyzing"
        :is-querying="isQuerying"
        class="mb-2"
      />

      <div
        v-if="error"
        class="flex flex-col items-center justify-center p-4 rounded-lg bg-r-50 dark:bg-r-900/20"
      >
        <span class="i-lucide-alert-circle text-r-500 text-2xl mb-2" />
        <p class="text-r-600 dark:text-r-400 text-center">
          {{ error.message }}
        </p>
        <button
          class="mt-3 px-4 py-2 text-sm font-medium text-n-slate-12 bg-n-solid-3 rounded-lg hover:bg-n-solid-4 transition-colors"
          @click="analyze('full_analysis')"
        >
          {{ $t('AGENTS_SIDECAR.ACTIONS.TRY_AGAIN') }}
        </button>
      </div>

      <AgentsEmptyState
        v-if="showEmptyState"
        @analyze="analyze('full_analysis')"
      />

      <template v-else>
        <AgentsEscalation
          v-if="escalation"
          :escalation="escalation"
          @apply-action="handleApplyAction"
        />

        <AgentsSummary v-if="summary" :summary="summary" />

        <AgentsDraftReply
          v-if="draftReply"
          :draft="draftReply"
          @use-draft="handleUseDraft"
        />

        <AgentsSuggestions
          v-if="
            suggestions.tags.length ||
            suggestions.actions.length ||
            suggestions.macros.length
          "
          :suggestions="suggestions"
          @apply-action="handleApplyAction"
        />

        <div v-if="hasChatHistory" class="mt-4 pt-4 border-t border-n-weak">
          <div class="flex items-center justify-between mb-4">
            <h4
              class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider flex items-center gap-2"
            >
              <span class="i-lucide-messages-square text-sm" />
              {{ $t('AGENTS_SIDECAR.QUERY.CHAT_HISTORY') }}
            </h4>
            <button
              class="text-xs text-n-slate-10 hover:text-n-slate-12 transition-colors flex items-center gap-1"
              @click="clearChatHistory"
            >
              <span class="i-lucide-trash-2 text-xs" />
              {{ $t('AGENTS_SIDECAR.QUERY.CLEAR_HISTORY') }}
            </button>
          </div>
          <div class="flex flex-col gap-6">
            <div
              v-for="item in chatHistory"
              :key="item.id"
              class="flex flex-col gap-3 animate-fade-in-up"
            >
              <div v-if="item.query" class="flex justify-end items-end gap-2">
                <div class="flex flex-col items-end max-w-[85%]">
                  <div
                    class="px-4 py-2.5 rounded-2xl rounded-br-md bg-n-blue-9 text-white shadow-sm"
                  >
                    <p class="text-sm leading-relaxed">{{ item.query }}</p>
                  </div>
                  <span
                    v-if="item.timestamp"
                    class="text-xxs text-n-slate-9 mt-1.5 px-1"
                  >
                    {{ formatTimestamp(item.timestamp) }}
                  </span>
                </div>
              </div>
              <div
                v-if="item.answer"
                class="flex justify-start items-start gap-2.5"
              >
                <div
                  class="flex-shrink-0 w-7 h-7 rounded-full bg-n-solid-3 border border-n-weak flex items-center justify-center"
                >
                  <span class="i-lucide-bot text-n-slate-11 text-sm" />
                </div>
                <div class="flex flex-col max-w-[calc(85%-36px)]">
                  <div
                    class="px-4 py-3 rounded-2xl rounded-tl-md bg-n-solid-2 border border-n-weak/50 shadow-sm"
                  >
                    <p
                      class="text-sm text-n-slate-12 leading-relaxed whitespace-pre-wrap"
                    >
                      {{ item.answer }}
                    </p>
                    <div
                      v-if="item.sources?.length"
                      class="mt-2.5 pt-2.5 border-t border-n-weak/50"
                    >
                      <p
                        class="text-xs text-n-slate-10 flex items-center gap-1.5"
                      >
                        <span class="i-lucide-file-text text-xs" />
                        {{
                          $t(
                            'AGENTS_SIDECAR.QUERY.SOURCES',
                            item.sources.length
                          )
                        }}
                      </p>
                    </div>
                  </div>
                  <span
                    v-if="item.timestamp"
                    class="text-xxs text-n-slate-9 mt-1.5 px-1"
                  >
                    {{ formatTimestamp(item.timestamp) }}
                  </span>
                </div>
              </div>
              <div
                v-if="item.isPending"
                class="flex justify-start items-start gap-2.5"
              >
                <div
                  class="flex-shrink-0 w-7 h-7 rounded-full bg-n-solid-3 border border-n-weak flex items-center justify-center"
                >
                  <span
                    class="i-lucide-bot text-n-slate-11 text-sm animate-pulse"
                  />
                </div>
                <div class="flex flex-col">
                  <div
                    class="px-4 py-3 rounded-2xl rounded-tl-md bg-n-solid-2 border border-n-weak/50 shadow-sm"
                  >
                    <span class="inline-flex gap-1">
                      <span
                        class="w-2 h-2 rounded-full bg-n-slate-9 animate-bounce"
                      />
                      <span
                        class="w-2 h-2 rounded-full bg-n-slate-9 animate-bounce [animation-delay:150ms]"
                      />
                      <span
                        class="w-2 h-2 rounded-full bg-n-slate-9 animate-bounce [animation-delay:300ms]"
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="isQuerying && queryAnswer?.isPartial"
            class="flex flex-col gap-3 mt-6 animate-fade-in-up"
          >
            <div class="flex justify-start items-start gap-2.5">
              <div
                class="flex-shrink-0 w-7 h-7 rounded-full bg-n-solid-3 border border-n-weak flex items-center justify-center"
              >
                <span
                  class="i-lucide-bot text-n-slate-11 text-sm animate-pulse"
                />
              </div>
              <div class="flex flex-col max-w-[calc(85%-36px)]">
                <div
                  class="px-4 py-3 rounded-2xl rounded-tl-md bg-n-solid-2 border border-n-weak/50 shadow-sm"
                >
                  <p
                    class="text-sm text-n-slate-12 leading-relaxed whitespace-pre-wrap"
                  >
                    {{ queryAnswer.text }}
                  </p>
                  <span class="inline-flex gap-1 mt-2">
                    <span
                      class="w-1.5 h-1.5 rounded-full bg-n-slate-9 animate-loader-pulse"
                    />
                    <span
                      class="w-1.5 h-1.5 rounded-full bg-n-slate-9 animate-loader-pulse [animation-delay:150ms]"
                    />
                    <span
                      class="w-1.5 h-1.5 rounded-full bg-n-slate-9 animate-loader-pulse [animation-delay:300ms]"
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <AgentsConfig :visible="showConfig" @close="showConfig = false" />

    <!-- Query Input at the bottom -->
    <div class="px-3 py-3 border-t border-n-weak bg-n-solid-1">
      <AgentsGlobalQuery :is-loading="isQuerying" @query="handleQuery" />
    </div>
  </div>
</template>
